/**
 * AvatarView.jsx — Remote video track renderer for avatar
 *
 * The avatar is rendered server-side by the LiveKit agent and published
 * as a regular video track into the LiveKit room. This component simply
 * attaches that remote video track to a <video> element — no client-side
 * SDK, no WASM, no WebGL.
 *
 * @param {object} props
 * @param {import('livekit-client').RemoteVideoTrack} props.videoTrack — The remote video track to render
 * @param {function} [props.onReady]  — Called when the video starts playing
 * @param {function} [props.onError]  — Called if video fails or times out
 */
import React, { useEffect, useRef, useState } from 'react';

const AVATAR_TIMEOUT_MS = 8000; // Fall back to voice-only after 8s with no video frames

export default function AvatarView({ videoTrack, onReady, onError }) {
  const videoRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const readyFired = useRef(false);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoTrack || !videoEl) return;

    let cancelled = false;
    readyFired.current = false;

    const markReady = () => {
      if (!cancelled && !readyFired.current) {
        readyFired.current = true;
        console.log('[AvatarView] ✅ Video ready!');
        setStatus('ready');
        onReady?.();
      }
    };

    const markError = (reason) => {
      if (!cancelled && !readyFired.current) {
        readyFired.current = true;
        console.warn('[AvatarView] ❌ Avatar failed:', reason);
        setStatus('error');
        onError?.(new Error(reason));
      }
    };

    // Attach the LiveKit video track — this sets srcObject and calls play()
    videoTrack.attach(videoEl);
    console.log('[AvatarView] Track attached to <video>', {
      readyState: videoEl.readyState,
      paused: videoEl.paused,
      srcObject: !!videoEl.srcObject,
      mediaStreamTracks: videoEl.srcObject?.getTracks?.()?.length,
    });

    // Listen for video events
    const onPlaying = () => markReady();
    const onLoadedData = () => markReady();
    const onErrorEvent = (e) => markError(`Video element error: ${e.type}`);

    videoEl.addEventListener('playing', onPlaying);
    videoEl.addEventListener('loadeddata', onLoadedData);
    videoEl.addEventListener('error', onErrorEvent);

    // If already has data (e.g. re-render with same track), fire immediately
    if (videoEl.readyState >= 2) {
      markReady();
    }

    // Timeout: if no frames arrive within AVATAR_TIMEOUT_MS, give up.
    // This handles the case where the agent participant publishes a track
    // that has no actual video data (e.g. old SpatialReal animation data).
    const timeout = setTimeout(() => {
      if (!readyFired.current) {
        markError(`No video frames received within ${AVATAR_TIMEOUT_MS}ms`);
      }
    }, AVATAR_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      videoEl.removeEventListener('playing', onPlaying);
      videoEl.removeEventListener('loadeddata', onLoadedData);
      videoEl.removeEventListener('error', onErrorEvent);
      videoTrack.detach(videoEl);
    };
  }, [videoTrack, onReady, onError]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: status === 'ready' ? 1 : 0,
          transition: 'opacity 0.5s ease',
        }}
      />
      {/* Loading indicator — subtle typing dots (no text) */}
      {status === 'loading' && (
        <div style={styles.overlay}>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-white/70 animate-typing-dot-1" />
            <span className="w-2 h-2 rounded-full bg-white/70 animate-typing-dot-2" />
            <span className="w-2 h-2 rounded-full bg-white/70 animate-typing-dot-3" />
          </div>
        </div>
      )}
      {/* Error — parent (VideoCallView) will fall back to voice-only */}
      {status === 'error' && null}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(15, 23, 42, 0.8)',
  },
};
